import {Box, Card, CardContent, Chip, Grid2, Typography, Paper} from "@mui/material";
import React from "react";
import {RiskReportTypes} from "../../types";
import {
Place
} from '@mui/icons-material';
import palette from "../../theme/palette";
import {getSafetyScoreColor} from "../../helpers/crimeScore";

interface Props {
    assessmentData : RiskReportTypes.RiskResponse;
}

const AREA_SAFETY_THRESHOLD = 60;


const getMunicipalitySafetyDesc = (desc:string)=>{
    try{
        const regex = /\((.*?)\)/;
        const match = regex.exec(desc);
        return match ? match[1] : desc;
    }catch(e){
        return desc;
    }
}


export const LocationAndArea: React.FC<Props> = ({
                                                     assessmentData
                                                 }) => {
    const { riskAssessmentInfo, sexOffenderNearby, sexOffenderAtLocation, dataToReview } = assessmentData;
    const { findings } = riskAssessmentInfo ?? {};
    // See news-articles.tsx — `findings` can be missing on stored reports.
    const { addressInfo } = findings ?? {};

    return(
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
                <Box className={'pdf-keep-with-next'} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: palette.darkBlue, p: 1, borderRadius: 1, mr: 2 }}>
                        <Place sx={{ color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={500}>
                            Location & Area Safety
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Environmental risk assessment
                        </Typography>
                    </Box>
                </Box>

                <Grid2 container spacing={3}>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Paper className={'pdf-avoid-break'} sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                            <Typography variant="h4" fontWeight={600} sx={{ mb: 1, color : getSafetyScoreColor(addressInfo?.propertyInfo?.address?.areaSafetyRating) }}>
                                {addressInfo?.propertyInfo?.address?.areaSafetyRating ? addressInfo?.propertyInfo?.address?.areaSafetyRating + '/100' : 'N/A'}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Area Safety Rating
                            </Typography>
                            <Typography variant="body2" fontSize={12}>
                                {addressInfo?.propertyInfo?.address?.areaSafetyDescription ?? 'Area Safety Rating Not Available'}
                            </Typography>
                        </Paper>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Paper className={'pdf-avoid-break'} sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                            <Typography variant="h4" fontWeight={600} sx={{ mb: 1, color : getSafetyScoreColor(addressInfo?.propertyInfo?.address?.municipalitySafetyRating) }}>
                                {addressInfo?.propertyInfo?.address?.municipalitySafetyRating ? addressInfo?.propertyInfo?.address?.municipalitySafetyRating + '/100' : 'N/A'}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Municipality Rating
                            </Typography>
                            <Typography variant="body2" fontSize={12}>
                                {addressInfo?.propertyInfo?.details?.municipalityInfo?.municipality && addressInfo?.propertyInfo?.details?.municipalityInfo?.state && addressInfo?.propertyInfo?.address?.municipalitySafetyDescription
                                    ? (
                                        <>
                                            {addressInfo?.propertyInfo?.details?.municipalityInfo?.municipality}, {addressInfo?.propertyInfo?.details?.municipalityInfo?.state} - {getMunicipalitySafetyDesc(addressInfo?.propertyInfo?.address?.municipalitySafetyDescription)}
                                        </>
                                    )
                                    : 'Municipality Safety Rating Not Available'}
                            </Typography>
                        </Paper>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Paper className={'pdf-avoid-break'} sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                            <Typography variant="h4" fontWeight={600} color="info" sx={{ mb: 1 }}>
                                {riskAssessmentInfo?.fbiCrimeData?.crimeRatePercentage ?? 'N/A'}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Crime Statistics
                            </Typography>
                            <Typography variant="body2" fontSize={12}>
                                {riskAssessmentInfo?.fbiCrimeData?.incidentsPer100K ? riskAssessmentInfo?.fbiCrimeData?.incidentsPer100K + ' per 100K residents' : 'Statistics Not Available'}
                            </Typography>
                        </Paper>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Paper className={'pdf-avoid-break'} sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                            <Typography variant="h4" fontWeight={600} sx={{ mb: 1, color: '#01579B' }}>
                                {sexOffenderNearby}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                Sex Offenders
                            </Typography>
                            <Typography variant="body2" fontSize={12}>
                                {sexOffenderNearby ? 'Nearby' : 'No offenders nearby'}, {sexOffenderAtLocation?.length ? `with ${sexOffenderAtLocation.length} at this location` : 'with 0 at this location'}
                            </Typography>
                        </Paper>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    )
}